<template>
  <PageWrapper :isLoading="isLoading" @submitFilters="handleSubmitFilters">
    <div>
      <CreateLevelHeader
          title="ایجاد سطح"
          button-text="افزودن سطح"
          primary-button-variant="success"
          @button-click="handleOpenCreateModal"
      />
    </div>

    <CustomTable
        :pageNumber="filters.pageNumber"
        :count="filters.count"
        :totalCount="totalRows"
        :tableColumns="tableColumns"
        :tableItems="tableRows"
        @changePage="handleChangePage"
    >
      <template #priority="data">
        <span class="lvl-chip">{{ formatNumber(data.item.priority) }}</span>
      </template>

      <template #avatar="data">
        <div class="lvl-avatarCell">
          <div class="lvl-avatarBadge" :class="getAvatarMeta(data.item.avatar).colorClass">
            <img
                v-if="getAvatarMeta(data.item.avatar).src"
                :src="getAvatarMeta(data.item.avatar).src"
                class="lvl-avatarImg"
                alt=""
            />
            <feather-icon v-else :icon="getAvatarMeta(data.item.avatar).icon" size="16" />
          </div>
        </div>
      </template>

      <template #discount="data">
        <span class="lvl-chip lvl-chip--green">{{ formatNumber(data.item.discount) }}%</span>
      </template>

      <template #upperRule="data">
        <div class="lvl-ruleCell" v-if="hasRule(data.item.upperMonth, data.item.upperValue)">
          <div class="lvl-ruleLine">
            <span class="lvl-chip">{{ formatNumber(data.item.upperMonth) }} ماه متوالی</span>
          </div>
          <div class="lvl-ruleLine">
            <span class="lvl-chip lvl-chip--soft">
              بیشتر یا برابر با  {{ formatNumber(data.item.upperValue) }} ساعت فعالیت
            </span>
          </div>
        </div>
        <span v-else class="text-muted">—</span>
      </template>

      <template #lowerRule="data">
        <div class="lvl-ruleCell" v-if="hasRule(data.item.lowerMonth, data.item.lowerValue)">
          <div class="lvl-ruleLine">
            <span class="lvl-chip">{{ formatNumber(data.item.lowerMonth) }} ماه متوالی</span>
          </div>
          <div class="lvl-ruleLine">
            <span class="lvl-chip lvl-chip--soft">
            کمتر یا برابر با {{ formatNumber(data.item.lowerValue) }} ساعت فعالیت
            </span>
          </div>
        </div>
        <span v-else class="text-muted">—</span>
      </template>

      <template #actions="data">
        <div class="lvl-actionsCell">
          <b-button
              variant="light"
              class="lvl-iconBtn"
              :disabled="isLoading"
              @click="handleOpenEditModal(data.item)"
              v-b-tooltip.hover
              title="ویرایش سطح"
          >
            <feather-icon icon="Edit2Icon" size="16" />
          </b-button>

          <b-button
              variant="light"
              class="lvl-iconBtn lvl-iconBtn--danger"
              :disabled="isLoading"
              @click="handleConfirmDelete(data.item)"
              v-b-tooltip.hover
              title="حذف سطح"
          >
            <feather-icon icon="TrashIcon" size="16" />
          </b-button>
        </div>
      </template>

      <template #table-busy>
        <div class="text-center my-2">در حال دریافت اطلاعات...</div>
      </template>
    </CustomTable>

    <b-modal id="delete-level-modal" centered size="sm" hide-header-close @hidden="handleDeleteModalClose">
      <template #modal-title>
        <div class="delete-modal-title">تایید حذف</div>
      </template>

      <div v-if="selectedItem" class="delete-modal-content">
        <p>آیا از حذف سطح "{{ selectedItem.name }}" مطمئن هستید؟</p>
        <small class="text-muted">این عمل قابل بازگشت نیست.</small>
      </div>

      <template #modal-footer>
        <div class="delete-modal-footer">
          <b-button variant="outline-secondary" class="delete-modal-btn" @click="$bvModal.hide('delete-level-modal')">
            انصراف
          </b-button>

          <b-button
              variant="danger"
              class="delete-modal-btn delete-modal-btn--danger"
              :disabled="isLoading"
              @click="handleDeleteItem"
          >
            حذف
          </b-button>
        </div>
      </template>
    </b-modal>

    <CreateLevelModal
        ref="createLevelModal"
        :submitting="isSaving"
        @submit="handleModalSubmit"
        @cancel="handleModalCancel"
    />
  </PageWrapper>
</template>

<script>
import { BButton, BModal, VBTooltip } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import CreateLevelModal from "@core/components/loyalty/CreateLevelModal.vue";
import CreateLevelHeader from "@core/components/loyalty/CreateLevelHeader.vue";
import { Create, Update, Delete, GetById, List } from "@/libs/Api/LevelLoyalty";
import PageWrapper from "@/views/components/Utils/PageWrapper.vue";
import CustomTable from "@/views/components/Utils/CustomTable.vue";
import { levelAvatarOptions, getLevelAvatarMeta } from "@core/utils/levelAvatars";

export default {
  name: "LevelLoyaltyPage",

  components: {
    PageWrapper,
    CustomTable,
    CreateLevelModal,
    CreateLevelHeader,
    BButton,
    BModal,
  },

  directives: {
    "b-tooltip": VBTooltip,
  },

  data() {
    return {
      isLoading: false,
      isSaving: false,
      totalRows: 0,
      rawItems: [],
      selectedItem: null,
      filters: {
        pageNumber: 1,
        count: 10,
        search: "",
      },
      tableColumns: [
        { label: "اولویت", key: "priority", thStyle: { width: "110px" } },
        { label: "آواتار", key: "avatar", thStyle: { width: "220px" } },
        { label: "نام سطح", key: "name" },
        { label: "تخفیف", key: "discount", thStyle: { width: "120px" } },
        { label: "شرایط ارتقا", key: "upperRule", thStyle: { width: "240px" } },
        { label: "شرایط تنزل", key: "lowerRule", thStyle: { width: "240px" } },
        { label: "عملیات", key: "actions", thStyle: { width: "120px" } },
      ],
      avatarOptions: levelAvatarOptions,
    };
  },

  computed: {
    tableRows() {
      return this.rawItems.map((item, index) => ({
        rowKey: `${item.id}-${index}`,
        ...item,
      }));
    },
  },

  watch: {
    "filters.search": function () {
      this.handleFilterChange();
    },
  },

  mounted() {
    this.fetchList();
  },

  methods: {
    handleFilterChange() {
      this.filters.pageNumber = 1;
      this.fetchList();
    },

    handleSubmitFilters() {
      this.filters.pageNumber = 1;
      this.fetchList();
    },

    handleChangePage(pageNumber) {
      if (pageNumber === this.filters.pageNumber) return;
      this.filters.pageNumber = pageNumber;
      this.fetchList();
    },

    showToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        position: "bottom-center",
        props: { title, text, icon: "", variant },
      });
    },

    formatNumber(value) {
      const num = Number(value || 0);
      try {
        return new Intl.NumberFormat("fa-IR").format(num);
      } catch {
        return String(num);
      }
    },

    getAvatarMeta(key) {
      return getLevelAvatarMeta(key);
    },

    getCompareLabel(value) {
      const n = Number(value ?? 0);
      if (n === 0) return "برابر با";
      if (n === 1) return "بیشتر یا برابر با";
      if (n === 2) return "کمتر یا برابر با";
      if (n === 3) return "بیشتر از";
      if (n === 4) return "کمتر از";
      return "≥";
    },

    hasRule(month, value) {
      return Number(month || 0) > 0 || Number(value || 0) > 0;
    },

    handleOpenCreateModal() {
      const modal = this.$refs.createLevelModal;
      if (!modal) return;
      modal.openForCreate();
      this.$nextTick(() => this.$bvModal.show("create-level-modal"));
    },

    async handleOpenEditModal(item) {
      if (!item || !item.id) {
        this.showToast("danger", "خطا", "اطلاعات سطح نامعتبر است.");
        return;
      }

      try {
        this.isLoading = true;

        const levelId = Number(item.id);
        const request = new GetById(this);
        request.setRequestParam({ LevelLoyaltyId: levelId });

        const response = await new Promise((resolve, reject) => {
          request.send(resolve, reject);
        });

        let responseItem = null;
        if (response?.data?.data) responseItem = response.data.data;
        else if (response?.data) responseItem = response.data;
        else if (response) responseItem = response;

        if (!responseItem || !responseItem.id) responseItem = item;

        const normalizedItem = {
          id: Number(responseItem.id || 0),
          priority: Number(responseItem.priority || 0),
          name: String(responseItem.name || ""),
          avatar: String(responseItem.avatar || ""),
          discount: Number(responseItem.discount || 0),
          upperMonth: Number(responseItem.upperMonth || 0),
          upperUnit: Number(responseItem.upperUnit || 0),
          upperValue: Number(responseItem.upperValue || 0),
          lowerMonth: Number(responseItem.lowerMonth || 0),
          lowerUnit: Number(responseItem.lowerUnit || 0),
          lowerValue: Number(responseItem.lowerValue || 0),
        };

        const modal = this.$refs.createLevelModal;
        if (!modal) return;

        modal.openForEdit(normalizedItem);
        this.$nextTick(() => this.$bvModal.show("create-level-modal"));
      } catch (error) {
        console.error("خطا:", error);
        this.showToast("danger", "خطا", "دریافت اطلاعات برای ویرایش انجام نشد.");
      } finally {
        this.isLoading = false;
      }
    },

    handleModalCancel() {
      this.$bvModal.hide("create-level-modal");
      const modal = this.$refs.createLevelModal;
      if (modal) modal.reset();
    },

    handleModalSubmit(payload) {
      const id = Number(payload.id || 0);
      if (id > 0) this.updateItem(payload);
      else this.createItem(payload);
    },

    async createItem(payload) {
      try {
        this.isSaving = true;

        const createPayload = {
          priority: Number(payload.priority || 0),
          name: String(payload.name || "").trim(),
          avatar: String(payload.avatar || ""),
          discount: Number(payload.discount || 0),
        };

        if (payload.upperMonth !== undefined) createPayload.upperMonth = Number(payload.upperMonth || 0);
        if (payload.upperUnit !== undefined) createPayload.upperUnit = Number(payload.upperUnit || 0);
        if (payload.upperValue !== undefined) createPayload.upperValue = Number(payload.upperValue || 0);
        if (payload.lowerMonth !== undefined) createPayload.lowerMonth = Number(payload.lowerMonth || 0);
        if (payload.lowerUnit !== undefined) createPayload.lowerUnit = Number(payload.lowerUnit || 0);
        if (payload.lowerValue !== undefined) createPayload.lowerValue = Number(payload.lowerValue || 0);

        const request = new Create(this);
        request.setRequestParamDataObj(createPayload);

        await new Promise((resolve, reject) => {
          request.send(resolve, reject);
        });

        this.showToast("success", "موفق", "سطح جدید با موفقیت ایجاد شد");
        this.$bvModal.hide("create-level-modal");

        const modal = this.$refs.createLevelModal;
        if (modal) modal.reset();

        this.filters.pageNumber = 1;
        await this.fetchList();
      } catch (error) {
        const errorMsg = error?.response?.data?.errors || error?.message || "ایجاد سطح انجام نشد.";
        this.showToast("danger", "خطا", errorMsg);
      } finally {
        this.isSaving = false;
      }
    },

    async updateItem(payload) {
      try {
        this.isSaving = true;

        const updatePayload = {
          id: Number(payload.id || 0),
          priority: Number(payload.priority || 0),
          name: String(payload.name || "").trim(),
          avatar: String(payload.avatar || ""),
          discount: Number(payload.discount || 0),
        };

        if (payload.upperMonth !== undefined) updatePayload.upperMonth = Number(payload.upperMonth || 0);
        if (payload.upperUnit !== undefined) updatePayload.upperUnit = Number(payload.upperUnit || 0);
        if (payload.upperValue !== undefined) updatePayload.upperValue = Number(payload.upperValue || 0);
        if (payload.lowerMonth !== undefined) updatePayload.lowerMonth = Number(payload.lowerMonth || 0);
        if (payload.lowerUnit !== undefined) updatePayload.lowerUnit = Number(payload.lowerUnit || 0);
        if (payload.lowerValue !== undefined) updatePayload.lowerValue = Number(payload.lowerValue || 0);

        const request = new Update(this);
        request.setRequestParamDataObj(updatePayload);

        await new Promise((resolve, reject) => {
          request.send(resolve, reject);
        });

        this.showToast("success", "موفق", "ویرایش با موفقیت انجام شد");
        this.$bvModal.hide("create-level-modal");

        const modal = this.$refs.createLevelModal;
        if (modal) modal.reset();

        await this.fetchList();
      } catch (error) {
        const errorMsg = error?.response?.data?.errors || error?.message || "ویرایش انجام نشد.";
        this.showToast("danger", "خطا", errorMsg);
      } finally {
        this.isSaving = false;
      }
    },

    handleConfirmDelete(item) {
      this.selectedItem = item;
      this.$bvModal.show("delete-level-modal");
    },

    handleDeleteModalClose() {
      this.selectedItem = null;
    },

    async handleDeleteItem() {
      if (!this.selectedItem) return;

      try {
        this.isLoading = true;

        const request = new Delete(this);
        request.setRequestParamDataObj({ LevelLoyaltyId: Number(this.selectedItem.id) });

        await new Promise((resolve, reject) => {
          request.send(resolve, reject);
        });

        this.showToast("success", "موفق", "حذف با موفقیت انجام شد");
        this.rawItems = this.rawItems.filter((x) => Number(x.id) !== Number(this.selectedItem.id));
        this.totalRows = Math.max(0, this.totalRows - 1);

        if (this.rawItems.length === 0 && this.filters.pageNumber > 1) {
          this.filters.pageNumber -= 1;
          await this.fetchList();
        }

        this.$bvModal.hide("delete-level-modal");
      } catch (error) {
        console.error("Error deleting item:", error);
        const errorMsg = error?.response?.data?.errors || error?.message || "حذف انجام نشد.";
        this.showToast("danger", "خطا", errorMsg);
      } finally {
        this.isLoading = false;
        this.handleDeleteModalClose();
      }
    },

    async fetchList() {
      try {
        this.isLoading = true;

        const request = new List(this);
        const params = {
          PageNumber: Math.max(1, this.filters.pageNumber),
          Count: this.filters.count,
        };

        if (String(this.filters.search || "").trim()) {
          params.Search = String(this.filters.search).trim();
        }

        request.setRequestParam(params);

        const response = await new Promise((resolve, reject) => {
          request.send(resolve, reject);
        });

        if (!response) {
          this.handleFetchError("دریافت اطلاعات انجام نشد.");
          return;
        }

        if (response.isSuccess === false) {
          this.handleFetchError(response.errors || "دریافت اطلاعات با خطا مواجه شد");
          return;
        }

        this.handleFetchSuccess(response);
      } catch (error) {
        console.error("Error fetching list:", error);
        this.handleFetchError("خطا در ارتباط با سرور");
      } finally {
        this.isLoading = false;
      }
    },

    handleFetchSuccess(response) {
      let items = [];
      let total = 0;

      if (response?.data?.data && Array.isArray(response.data.data)) {
        items = response.data.data;
        total = Number(response.data.total || 0);
      } else if (response?.data && Array.isArray(response.data)) {
        items = response.data;
        total = items.length;
      } else if (Array.isArray(response)) {
        items = response;
        total = items.length;
      }

      this.totalRows = total;
      this.rawItems = items.map((item) => ({
        id: Number(item.id || 0),
        priority: Number(item.priority || 0),
        name: String(item.name || ""),
        avatar: String(item.avatar || ""),
        discount: Number(item.discount || 0),
        upperMonth: Number(item.upperMonth || 0),
        upperUnit: Number(item.upperUnit || 0),
        upperValue: Number(item.upperValue || 0),
        lowerMonth: Number(item.lowerMonth || 0),
        lowerUnit: Number(item.lowerUnit || 0),
        lowerValue: Number(item.lowerValue || 0),
      }));
    },

    handleFetchError(errorMessage) {
      this.totalRows = 0;
      this.rawItems = [];
      this.showToast("danger", "خطا", errorMessage);
    },
  },
};
</script>

<style scoped>
.lvl-avatarCell {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
}

.lvl-avatarBadge {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.lvl-avatarImg {
  width: 40px;
  height: 40px;
  object-fit: contain;
  display: block;
}

.lvl-avatarText__title {
  font-weight: 900;
  font-size: 13px;
  color: #111827;
}

.lvl-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  background: #f3f4f6;
  font-weight: 900;
  font-size: 12px;
  color: #111827;
  white-space: nowrap;
}

.lvl-chip--soft {
  background: rgba(59, 130, 246, 0.1);
  color: #1e40af;
}

.lvl-chip--green {
  background: rgba(22, 163, 74, 0.12);
  color: #166534;
}

.lvl-ruleCell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lvl-ruleLine {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lvl-actionsCell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lvl-iconBtn {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #e5e7eb;
  background: #fff;
  transition: all 0.2s ease;
}

.lvl-iconBtn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.lvl-iconBtn--danger {
  color: #b91c1c;
  background: rgba(185, 28, 28, 0.06) !important;
  border-color: rgba(185, 28, 28, 0.18);
}

.lvl-iconBtn--danger:hover:not(:disabled) {
  background: rgba(185, 28, 28, 0.12) !important;
  border-color: rgba(185, 28, 28, 0.28);
}

.delete-modal-title {
  font-size: 16px;
  font-weight: 900;
  color: #111827;
}

.delete-modal-content {
  text-align: center;
  padding: 10px 0;
}

.delete-modal-content p {
  margin-bottom: 8px;
  font-size: 14px;
  color: #374151;
}

.delete-modal-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.delete-modal-btn {
  height: 42px;
  border-radius: 12px;
  font-weight: 900;
  font-size: 13px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.delete-modal-btn--danger {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #fff;
  border: none;
}

.delete-modal-btn--danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  transform: translateY(-1px);
}

.lvl-c--neutral {
  background: #f3f4f6;
  color: #6b7280;
}
.lvl-c--bronze {
  background: linear-gradient(135deg, rgba(180, 83, 9, 0.2), rgba(245, 158, 11, 0.22));
  color: #b45309;
}
.lvl-c--silver {
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.18), rgba(148, 163, 184, 0.22));
  color: #6b7280;
}
.lvl-c--gold {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.22), rgba(251, 191, 36, 0.24));
  color: #d97706;
}
.lvl-c--diamond {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.18), rgba(99, 102, 241, 0.22));
  color: #3b82f6;
}
.lvl-c--platinum {
  background: linear-gradient(135deg, rgba(20, 184, 166, 0.18), rgba(94, 234, 212, 0.22));
  color: #14b8a6;
}
.lvl-c--gladiator {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.14), rgba(244, 63, 94, 0.16));
  color: #dc2626;
}
.lvl-c--elite {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.16), rgba(16, 185, 129, 0.18));
  color: #10b981;
}
</style>
